// Initialize Sentry as early as possible
import { Sentry, captureException } from "@/shared/lib/sentry";
import { isNetworkError } from "@/shared/lib/errorUtils";

// Backstop against Radix FocusScope focus ping-pong (see Sentry 131410963/131410960).
// The root cause — duplicate @radix-ui/react-focus-scope copies not sharing a single
// focusScopesStack singleton — is fixed via package.json "overrides". This bounded-depth
// guard on HTMLElement.prototype.focus is a permanent runtime safety net so that any future
// duplicate-copy regression degrades gracefully instead of throwing "Maximum call stack size
// exceeded". Installed before @react-aria/interactions (loaded lazily by Headless UI) wraps
// focus, so react-aria's patch composes on top of this guard.
if (typeof HTMLElement !== "undefined") {
  const originalFocus = HTMLElement.prototype.focus;
  let depth = 0;
  let warned = false;
  const MAX_DEPTH = 30; // Deep enough for legit nested focus traps, far below the stack limit.
  HTMLElement.prototype.focus = function patchedFocus(
    this: HTMLElement,
    ...args
  ) {
    if (depth >= MAX_DEPTH) {
      // Break the recursion instead of overflowing the stack.
      if (!warned) {
        warned = true;
        // Rate-limited to once per session; surfaces a regression without spamming.
        const message =
          "[focus-guard] Aborted runaway HTMLElement.focus recursion (Radix FocusScope loop).";
        console.warn(message);
        // Report to Sentry so a future duplicate-copy regression is observable in
        // monitoring instead of being silently swallowed by this backstop.
        Sentry.captureMessage(message, {
          level: "error",
          tags: { source: "focus-guard" },
        });
      }
      return;
    }
    depth++;
    try {
      return originalFocus.apply(this, args as never);
    } finally {
      depth--;
    }
  } as typeof originalFocus;
}

// Global safety net for unhandled promise rejections (see Sentry ZERMMI-44).
// Some floating promises — e.g. an un-awaited supabase.auth.getSession() chain, or a
// raw PostgREST/GoTrue rejection whose reason is a plain { code, message } object — can
// reject outside TanStack Query's error path, bypassing the useSupabaseQuery/useSupabaseMutation
// wrappers. When the reason is a non-Error object, Sentry's default global handler reports it
// anonymously ("Object captured as promise rejection with keys: code, message"), which is
// unactionable. This listener normalizes the whole class centrally: transient connectivity
// faults are swallowed (consistent with the wrappers skipping network errors), and everything
// else is reported once through captureException — which converts a raw { code, message } into a
// real Error carrying the message — then preventDefault() so Sentry's auto handler doesn't also
// emit the raw anonymous duplicate. Genuine Error reasons are left to normal handling.
if (typeof window !== "undefined") {
  window.addEventListener("unhandledrejection", (event) => {
    const reason = event.reason;

    // Real Error instances already report cleanly via Sentry's default handling.
    if (reason instanceof Error) {
      return;
    }

    // Only take over anonymous, non-Error rejections (e.g. raw Supabase { code, message }).
    if (!reason || typeof reason !== "object") {
      return;
    }

    // Transient connectivity faults are noise, not app bugs — swallow them.
    if (isNetworkError(reason)) {
      event.preventDefault();
      return;
    }

    // Report a clean, message-bearing Error, then suppress the anonymous auto-report.
    captureException(reason, { source: "unhandledrejection" });
    event.preventDefault();
  });
}

import { ProtectedRoute } from "@/app/components/ProtectedRoute";
import { Root } from "@/app/Root";
import { initI18n } from "@/shared/i18n";
import { setupZodI18n } from "@/shared/lib/zodI18nSetup";
import { LoadingFallback } from "@/shared/ui/LoadingFallback";
import { RouteErrorElement } from "@/shared/ui/RouteErrorElement";
import { SuspenseWithErrorBoundary } from "@/shared/ui/SuspenseWithErrorBoundary";
// (removed QueryClient, QueryClientProvider import)
import { createRoot } from "react-dom/client";
import { createBrowserRouter, RouterProvider } from "react-router-dom";

import App from "./App";
import { appRoutes } from "./app/route-config";
import "./index.css";
import "react-phone-number-input/style.css";
import "./styles/calendar-export.css";
import "./styles/perio-export.css";

// --- Host-specific router: periochart subdomain should mount its page at "/" without showing "/periochart" ---
const host = typeof window !== "undefined" ? window.location.host : "";
const isPerioSubdomain =
  host === "periochart.zermmi.com" || host.startsWith("periochart.");
const perioRoute = appRoutes.find((r) => r.path === "/periochart");
const treatmentCatalogRoute = appRoutes.find(
  (r) => r.path === "/codes-catalog"
);
const reviewRoute = appRoutes.find((r) => r.path === "/reviews");

const isTreatmentsSubdomain =
  host === "treatments.zermmi.com" || host.startsWith("treatments.");
const isReviewsSubdomain =
  host === "reviews.zermmi.com" || host.startsWith("reviews.");

let router: ReturnType<typeof createBrowserRouter>;
if (isPerioSubdomain && perioRoute) {
  const PerioComponent = perioRoute.component as React.ComponentType<any>;
  router = createBrowserRouter([
    {
      element: <Root />,
      errorElement: <RouteErrorElement />,
      children: [
        {
          path: "/",
          element: (
            <SuspenseWithErrorBoundary fallback={<LoadingFallback />}>
              <PerioComponent />
            </SuspenseWithErrorBoundary>
          ),
        },
      ],
    },
  ]);
} else if (isReviewsSubdomain && reviewRoute) {
  const ReviewComponent = reviewRoute.component as React.ComponentType<any>;
  router = createBrowserRouter([
    {
      element: <Root />,
      errorElement: <RouteErrorElement />,
      children: [
        {
          path: "/",
          element: (
            <SuspenseWithErrorBoundary fallback={<LoadingFallback />}>
              <ReviewComponent />
            </SuspenseWithErrorBoundary>
          ),
        },
      ],
    },
  ]);
} else if (isTreatmentsSubdomain && treatmentCatalogRoute) {
  const TreatmentCatalogComponent =
    treatmentCatalogRoute.component as React.ComponentType<any>;
  router = createBrowserRouter([
    {
      element: <Root />,
      errorElement: <RouteErrorElement />,
      children: [
        {
          path: "/",
          element: (
            <SuspenseWithErrorBoundary fallback={<LoadingFallback />}>
              <TreatmentCatalogComponent />
            </SuspenseWithErrorBoundary>
          ),
        },
      ],
    },
  ]);
} else {
  // --- Generate Routes from Central Config ---

  const isStandalonePage = (route: (typeof appRoutes)[0]) =>
    route.path === "/login" ||
    route.path === "/signup" ||
    route.path === "/forgot-password" ||
    route.path === "/reset-password" ||
    route.path === "/accept-invite" ||
    route.path === "/booking" ||
    route.path === "/booking/:practiceId" ||
    route.path === "/r/:token" ||
    route.path === "/a/:token" ||
    route.path === "/patient/:token" ||
    route.path === "/patient/:token/form/:formId" ||
    route.path === "/patient/:token/reschedule" ||
    route.path === "/periochart" ||
    route.path === "/codes-catalog" ||
    route.path === "/reviews" ||
    route.path === "/analytics" ||
    route.path === "/upload/:token";

  const generateRoutes = (routes: typeof appRoutes): any[] => {
    return routes.map((route) => {
      const element = (
        <SuspenseWithErrorBoundary fallback={<LoadingFallback />}>
          <ProtectedRoute>
            <route.component />
          </ProtectedRoute>
        </SuspenseWithErrorBoundary>
      );

      const children = route.children ? generateRoutes(route.children) : [];

      const generatedPath = route.path === "/" ? undefined : route.path;

      return {
        path: generatedPath,
        index: route.path === "/",
        element: element,
        children: children.length > 0 ? children : undefined,
      };
    });
  };

  // Standalone routes that do not use the main <App /> layout
  const standaloneRoutes = appRoutes.filter(isStandalonePage).map((route) => {
    const element = (
      <SuspenseWithErrorBoundary fallback={<LoadingFallback />}>
        <route.component />
      </SuspenseWithErrorBoundary>
    );
    return {
      path: route.path,
      element:
        route.path === "/login" ||
        route.path === "/signup" ||
        route.path === "/forgot-password" ||
        route.path === "/reset-password" ||
        route.path === "/accept-invite" ||
        route.path === "/booking" ||
        route.path === "/booking/:practiceId" ||
        route.path === "/r/:token" ||
        route.path === "/a/:token" ||
        route.path === "/patient/:token" ||
        route.path === "/patient/:token/form/:formId" ||
        route.path === "/patient/:token/reschedule" ||
        route.path === "/periochart" ||
        route.path === "/codes-catalog" ||
        route.path === "/reviews" ||
        route.path === "/analytics" ||
        route.path === "/upload/:token" ? (
          element
        ) : (
          <ProtectedRoute>{element}</ProtectedRoute>
        ),
    };
  });

  // Routes that are rendered within the main <App /> layout
  const appShellRoutes = generateRoutes(
    appRoutes.filter((route) => !isStandalonePage(route))
  );

  // Ensure children rendered inside <App/> use relative paths so they mount in the AppShell's <Outlet>
  const relativizeRoutes = (routes: any[]): any[] =>
    routes.map((r) => ({
      ...r,
      path:
        typeof r.path === "string" && r.path.startsWith("/")
          ? r.path.slice(1)
          : r.path,
      children: r.children ? relativizeRoutes(r.children) : undefined,
    }));

  router = createBrowserRouter([
    {
      element: <Root />,
      errorElement: <RouteErrorElement />,
      children: [
        ...standaloneRoutes,
        {
          path: "/",
          element: <App />, // The main layout
          errorElement: <RouteErrorElement />,
          children: relativizeRoutes(appShellRoutes),
        },
      ],
    },
  ]);
}

// Initialize i18n before rendering the app

async function startApp() {
  // Get initial language from localStorage or URL params or default to 'en'
  const urlParams = new URLSearchParams(window.location.search);
  const urlLang = urlParams.get("lang");
  const storedLang = localStorage.getItem("preferred_language");
  const initialLanguage = (urlLang || storedLang || "en") as any;

  await initI18n(initialLanguage);

  // Set up Zod internationalization
  setupZodI18n();

  // Set HTML lang attribute
  document.documentElement.lang = initialLanguage;
  document.documentElement.dir = "ltr";

  const rootElement = document.querySelector("#root");
  if (!rootElement) {
    throw new Error("Root element not found");
  }

  createRoot(rootElement).render(
    // TEMPORARY: StrictMode disabled for render profiling
    // <StrictMode>
      <RouterProvider router={router} />
    // </StrictMode>
  );
}

startApp().catch(console.error);
